import { downloadInvoiceAsPdf, downloadInvoiceDraftAsPdf } from "../../common/invoiceDownloadHelper";
import { EmailStatus, EmailStatusPresentation, TimelineStatus } from "../features/email-tracking/email-tracking.types";
import countriesList from "countries-list";

// A few names stored in the app that differ from the countries-list `name`,
// so reverse-lookup still resolves the correct 2-letter code.
const COUNTRY_NAME_ALIASES: { [name: string]: string } = {
  usa: "US",
  "united states": "US",
  "united states of america": "US",
  uk: "GB",
  "united kingdom": "GB",
};

/**
 * Translate a country to the active language (e.g. "Sweden" → "Sverige" in sv-se)
 * using the shared COUNTRY_<code> translation keys. Resolves the 2-letter code
 * from an explicit `countryCode` when present, otherwise by reverse-looking-up
 * the English country name. Falls back to the original name if nothing matches,
 * so the stored value is never lost.
 */
export const getTranslatedCountryName = (
  t: (key: string, opts?: any) => string,
  country?: string,
  countryCode?: string
): string => {
  const name = (country || "").trim();
  let code = (countryCode || "").trim().toUpperCase();

  if (!code && name) {
    const lower = name.toLowerCase();
    code = COUNTRY_NAME_ALIASES[lower] || "";
    if (!code) {
      const all = (countriesList as any).countries || {};
      const found = Object.keys(all).find(
        (c) => (all[c]?.name || "").toLowerCase() === lower
      );
      if (found) code = found;
    }
  }

  if (code) {
    return t(`COUNTRY_${code}`, { defaultValue: name || code });
  }
  return name;
};

// Enums for invoice filtering
enum InvoicePurposeType {
    General = 1
}

enum InvoiceStatus {
    UnPaid = 1,
    Paid = 2,
    PartlyPaid = 3,
    Overdue = 4
}

enum InvoiceStage {
    Sent = 1,
    Completed = 2,
    Pending = 3
}
// Normalizes ',' (Swedish decimal separator) to '.', caps decimals at maxDecimals, and returns null for invalid input
export const sanitizePriceInput = (value: string, maxDecimals: number = 2): string | null => {
    if (value === '') {
        return value;
    }

    const normalized = value.replace(',', '.');

    if (!/^\d*\.?\d*$/.test(normalized)) {
        return null;
    }

    const dotIndex = normalized.indexOf('.');
    if (dotIndex !== -1 && normalized.length - dotIndex - 1 > maxDecimals) {
        return normalized.slice(0, dotIndex + 1 + maxDecimals);
    }

    return normalized;
};

export const formatAmount = (num: number) => {
    const isSwedish = new URLSearchParams(window.location.search).get('lang') === 'sv-se';
    return new Intl.NumberFormat(isSwedish ? 'sv-SE' : 'en-US').format(num);
};

/**
 * Format amount with locale-specific number formatting
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'SEK')
 * @param locale - Locale string (default: current language from localStorage)
 * @returns Formatted amount string with exactly 2 decimal places
 * 
 * Examples:
 * - sv-SE: 28 000,09 SEK
 * - en-US: 28,000.09 SEK
 */
export const formatAmountWithLocale = (
    amount: number,
    currency: string = 'SEK',
    locale?: string
): string => {
    // Get locale from localStorage if not provided
    const currentLocale = locale || localStorage.getItem('i18nextLng') || 'en-US';

    // Normalize locale string (sv-se -> sv-SE, en-us -> en-US)
    const normalizedLocale = currentLocale.includes('sv') ? 'sv-SE' : 'en-US';

    // Format with exactly 2 decimal places
    const formattedNumber = new Intl.NumberFormat(normalizedLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Math.abs(amount));

    return `${formattedNumber} ${currency}`;
};

export const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('sv-SE');
};

export const mapInvoiceStatus = (status: number): string => {
    switch (status) {
        case 2:
            return 'PAID';
        case 3:
            return 'PARTLY_PAID';
        case 4:
            return 'OVERDUE';
        default:
            return 'UNPAID';
    }
};
export const mapSalaryStatus = (status: number): string => {
    switch (status) {
        case 2:
            return 'PAID';
        case 3:
            return 'PARTLY_PAID';
        case 4:
            return 'OVERDUE';
        default:
            return 'UNPAID';
    }
};

export const getStatusBadgeClass = (status: number): string => {
    switch (status) {
        case 2:
            return 'invoice-table-detailed__badge--paid';
        case 4:
            return 'invoice-table-detailed__badge--overdue';
        case 3:
            return 'invoice-table-detailed__badge--partly-paid';
        case 1:
        default:
            return 'invoice-table-detailed__badge--unpaid';
    }
};

export const getStatusForSalaryStatus = (status: number): string => {
    switch (status) {
        case 1:
            return 'invoice-table-detailed__badge--paid';
        case 0:
        default:
            return 'invoice-table-detailed__badge--unpaid';
    }
};

export const getStageBadgeClass = (stage?: string | number): string => {
    // Handle string stage names
    if (typeof stage === 'string') {
        const upperStage = stage.toUpperCase();
        switch (upperStage) {
            case 'SENT':
                return 'invoice-table-detailed__stage-badge--sent';
            case 'COMPLETED':
                return 'invoice-table-detailed__stage-badge--completed';
            case 'PENDING':
                return 'invoice-table-detailed__stage-badge--pending';
            default:
            return  'invoice-table-detailed__stage-badge--pending';
        }
            
    }

    // Handle numeric stage values
    switch (stage) {
        case InvoiceStage.Sent:
        case 1:
            return 'invoice-table-detailed__stage-badge--sent';
        case InvoiceStage.Completed:
        case 2:
            return 'invoice-table-detailed__stage-badge--completed';
        case InvoiceStage.Pending:
        case 3:
        default:
            return 'invoice-table-detailed__stage-badge--pending';
    }
};



// ---------------------------------------------------------------------------
// Email tracking (SendGrid) — maps an EmailStatus to its i18n key + badge class.
// Mirrors the mapInvoiceStatus / getStatusBadgeClass pair above.
// ---------------------------------------------------------------------------
export const mapEmailStatus = (status?: EmailStatus | null): string => {
    switch (status) {
        case 'sent': return 'EMAIL_SENT';
        case 'delivered': return 'EMAIL_DELIVERED';
        case 'opened': return 'EMAIL_OPENED';
        case 'bounced': return 'EMAIL_BOUNCED';
        case 'failed': return 'EMAIL_FAILED';
        case 'not_sent':
        default:
            return 'EMAIL_NOT_SENT';
    }
};

export const getEmailStatusBadgeClass = (status?: EmailStatus | null): string =>
    `email-status-badge--${status || 'not_sent'}`;

/**
 * Header-pill label for the tracking modal's furthest-stage status — a superset
 * of mapEmailStatus that adds the two invoice-progress stages.
 */
export const mapTimelineStatus = (status?: TimelineStatus | null): string => {
    switch (status) {
        case 'paid': return 'PAID';
        case 'salary_created': return 'SALARY_CREATED';
        case 'salary_paid': return 'SALARY_PAID';
        default: return mapEmailStatus(status as EmailStatus | null | undefined);
    }
};

export const getTimelineStatusBadgeClass = (status?: TimelineStatus | null): string =>
    `email-status-badge--${status || 'not_sent'}`;

/**
 * Maps a raw SendGrid email status (from `InvoiceItem.emailStatus`) to a UI
 * presentation for the Manage Invoices EMAIL STATUS column. Case-insensitive
 * (backend sends lowercase, but we normalise defensively). Each raw value is
 * mapped to one of the six built visual variants (colour + icon) plus a
 * specific i18n label; null/unknown/unrecognised → hidden (muted placeholder).
 *
 * Colour groups (per product spec):
 *   success  → delivered / open / click        (teal / green)
 *   pending  → processed / deferred            (blue "sent")
 *   failure  → bounce (amber) · dropped / spamreport (red)
 *   opt-out  → unsubscribe / group_(un|re)subscribe (grey)
 *   none     → null / unknown                  (hidden)
 */
export const mapSendGridEmailStatus = (raw?: string | null): EmailStatusPresentation => {
    const value = (raw ?? '').trim().toLowerCase();
    switch (value) {
        // success
        case 'delivered': return { variant: 'delivered', labelKey: 'EMAIL_DELIVERED', hidden: false };
        case 'open': return { variant: 'opened', labelKey: 'EMAIL_OPENED', hidden: false };
        case 'click': return { variant: 'opened', labelKey: 'EMAIL_CLICKED', hidden: false };
        // in-progress / pending
        case 'processed': return { variant: 'sent', labelKey: 'EMAIL_PROCESSED', hidden: false };
        case 'deferred': return { variant: 'sent', labelKey: 'EMAIL_DEFERRED', hidden: false };
        // failure / warning
        case 'bounce': return { variant: 'bounced', labelKey: 'EMAIL_BOUNCED', hidden: false };
        case 'dropped': return { variant: 'failed', labelKey: 'EMAIL_DROPPED', hidden: false };
        case 'spamreport': return { variant: 'failed', labelKey: 'EMAIL_SPAMREPORT', hidden: false };
        // opt-out
        case 'unsubscribe':
        case 'group_unsubscribe': return { variant: 'not_sent', labelKey: 'EMAIL_UNSUBSCRIBED', hidden: false };
        case 'group_resubscribe': return { variant: 'not_sent', labelKey: 'EMAIL_RESUBSCRIBED', hidden: false };
        // no status / unrecognised
        case '':
        case 'unknown':
        default: return { variant: 'not_sent', labelKey: 'EMAIL_NOT_SENT', hidden: true };
    }
};


export const getExpressPaymentStatus = (invoice: any, isLoginWithBankId: boolean, t: any ) => {
    let title = "";
    let isDisabled = false;
    let iconName = ['Approved', 'NotApproved', 'NotApplied']
    let icon = iconName[2]
    const paymentTerm = invoice?.customer?.paymentTerm || invoice?.paymentTerm;
    const accountType = invoice?.customer?.accountType ?? invoice?.accountType;
    const country = invoice?.customer?.country || invoice?.country;

    if (!isLoginWithBankId) {
        console.log("isLoginWithBankId:", isLoginWithBankId);
        title = t("EXPRESS_PAYMENT_LOGIN_BANKID") || "To access express payment, please log in with BankID.";
        isDisabled = true;
        icon = iconName[2]
    } else if (paymentTerm === "45" || paymentTerm === "60") {
        title = t("EXPRESS_PAYMENT_45_60_DAYS") || "We don't accept express payment for 45 & 60 days.";
        isDisabled = true;
        icon = iconName[2]
    } else if (accountType === 1) {
        title = t("EXPRESS_PAYMENT_NO_PRIVATE") || "We don't accept express payment for private customers.";
        isDisabled = true;
        icon = iconName[2]
    } else if (country !== "Sweden" && accountType !== 1) {
        title = t("EXPRESS_PAYMENT_SWEDEN_ONLY") || "We don't accept express payments for customers outside Sweden.";
        isDisabled = true;
        icon = iconName[2]
    } else if (invoice?.amount > 60000) {
        title = t("EXPRESS_PAYMENT_LIMIT_60K") || "For Express Payment, there is a limit of SEK 60,000 per invoice.";
        isDisabled = true;
        icon = iconName[2]
    } else {
        title = t("EXPRESS_PAYMENT") || "Express Payment";
        isDisabled = false;
        icon = iconName[0]
    }

    return { title, isDisabled , icon };
};


export const handleDownloadInvoice = async (invoice: any, lang: string) => {
    try {
        await downloadInvoiceAsPdf(invoice, lang);
    } catch (error: any) {
        console.error('Download error:', error);
        alert(`Failed to download invoice: ${error.message}`);
    }
};

export const handleDownloadDraftInvoice = async (invoice: any, lang: string) => {
    try {
        await downloadInvoiceDraftAsPdf(invoice, lang);
    } catch (error: any) {
        console.error('Download error:', error);
        alert(`Failed to download invoice: ${error.message}`);
    }
};


export const formatDateForPayout = (
    prefillDate: string | number | Date,
    paymentTerm: boolean,
    language: string | undefined
) => {
    const currentDate = prefillDate ? new Date(prefillDate) : new Date();

    if (paymentTerm) {
        currentDate.setDate(currentDate.getDate() + Number(paymentTerm));
    }

    // Fix: language check should compare with actual values, not with an uninitialized variable
    const lang = language === 'sv-se' ? 'sv-se' : 'en-us';

    const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
    };

    return currentDate.toLocaleString(lang, options);
};


export const isGuestInvozioEmail = (email: string) => {
    try {
        const lowerEmail = email.toLowerCase();
        return lowerEmail.startsWith('guest') && lowerEmail.endsWith('@invozio.com');
    } catch (error: string | any) {
        console.error("Error checking guest email:", error.message);
        return false;
    }
};

export const sentInvoicesStatus = (invoice: any): string => {
    if (invoice.isEmailSent && invoice.invoiceStatus === InvoiceStatus.UnPaid && !invoice.isSalaryCreated) {
        return 'SENT';
    } else if (
        invoice.isEmailSent &&
        invoice.invoiceStatus !== InvoiceStatus.UnPaid &&
        (invoice.isSalaryCreated || invoice.isSalaryStatusUpdatedManually || invoice.invoiceSalaryStatus === InvoiceStatus.Paid)
    ) {
        return 'COMPLETED';
    } else if (!invoice.isEmailSent) {
        return 'PENDING';
    }

    return 'PENDING';
};
